import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import type { StateCreator } from "zustand";
import type { GetFeedArticlesPagedFromProjectionArticle } from "@/data-access/news-feeds/queries/articles-management-queries";
import type { FeedArticleProjectionOrdering } from "@/data-access/shared/types/feed-article-projection.types";
import type {
  ArticleEditState,
  FeedContentManagementSlice,
  PaginationState,
} from "./feed-content-management.slice";

type ArticlesManagementActions = Pick<
  FeedContentManagementSlice,
  | "setInitialLoading"
  | "setFetchingArticles"
  | "setError"
  | "setCurrentPage"
  | "appendArticles"
  | "resetState"
  | "setFeedVolume"
  | "setStackFeedVolumeBy"
  | "setActiveBar"
  | "setChartSelection"
  | "resetFeedVolumeView"
  | "toggleArticleSelection"
  | "setSelectedArticles"
  | "clearSelection"
  | "setIsAllSelected"
  | "setSingleArticleIdForAction"
  | "setDeleteModalOpen"
  | "setEditModalOpen"
  | "setRemoveArticleFromClusterModalOpen"
  | "setMoveArticleFromNarrativeClusterModalOpen"
  | "setNarrativeIntelligenceV2ModalOpen"
  | "setInsightsNarrativeClusterId"
  | "resetModalState"
  | "setDisplayArticleActionBar"
  | "deleteArticlesFromStore"
  | "resetSelectionAfterBulkAction"
  | "setEditField"
  | "setEditFields"
  | "clearEditFields"
  | "resetEditModal"
  | "populateEditFieldsForSingleArticle"
  | "updateArticlesInStore"
  | "setSearchTerm"
  | "setIsAdvancedSearch"
  | "setOrdering"
>;

export const createArticlesManagementActions: StateCreator<
  FeedContentManagementSlice,
  [["zustand/devtools", never]],
  [],
  ArticlesManagementActions
> = (set, get, _api) => ({
  setInitialLoading: (loading: boolean) => {
    set(
      { isInitialLoading: loading },
      false,
      "[User] Articles Management: Set initial loading state",
    );
  },

  setFetchingArticles: (fetching: boolean) => {
    set(
      { isFetchingArticles: fetching },
      false,
      "[User] Articles Management: Set fetching state",
    );
  },

  setError: (error: string | null) => {
    set({ error }, false, "[User] Articles Management: Set error state");
  },

  setCurrentPage: (
    edges: Array<{
      cursor: string;
      node: GetFeedArticlesPagedFromProjectionArticle;
    }>,
    pageInfo: PaginationState,
  ) => {
    const { articlesMap } = get();
    const newMap = new Map(articlesMap);

    // Add/update articles in map
    for (const { node } of edges) {
      newMap.set(node.id, node);
    }

    set(
      {
        articlesMap: newMap,
        currentPage: {
          edges,
          hasNextPage: pageInfo.hasNextPage,
          hasPreviousPage: pageInfo.hasPreviousPage,
        },
        pagination: pageInfo,
        isInitialLoading: false,
        isFetchingArticles: false,
        error: null,
      },
      false,
      "[User] Articles Management: Set current page",
    );
  },

  appendArticles: (
    edges: Array<{
      cursor: string;
      node: GetFeedArticlesPagedFromProjectionArticle;
    }>,
    pageInfo: PaginationState,
  ) => {
    const { articlesMap, currentPage } = get();
    const newMap = new Map(articlesMap);

    // Add/update articles in map
    for (const { node } of edges) {
      newMap.set(node.id, node);
    }

    // Append new edges to existing ones
    const combinedEdges = [...currentPage.edges, ...edges];

    set(
      {
        articlesMap: newMap,
        currentPage: {
          edges: combinedEdges,
          hasNextPage: pageInfo.hasNextPage,
          hasPreviousPage: pageInfo.hasPreviousPage,
        },
        pagination: pageInfo,
        isInitialLoading: false,
        isFetchingArticles: false,
        error: null,
      },
      false,
      "[User] Articles Management: Append articles",
    );
  },

  resetState: () => {
    const { searchTerm, isAdvancedSearch, ordering } = get();
    set(
      {
        articlesMap: new Map<
          string,
          GetFeedArticlesPagedFromProjectionArticle
        >(),
        currentPage: {
          edges: [],
          hasNextPage: false,
          hasPreviousPage: false,
        },
        isInitialLoading: false,
        isFetchingArticles: false,
        error: null,
        pagination: {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
          totalCount: null,
        },
        selectedArticleIds: new Set<string>(),
        singleArticleIdForAction: null,
        isAllSelected: false,
        isDeleteModalOpen: false,
        isEditModalOpen: false,
        isRemoveArticleFromClusterModalOpen: false,
        isMoveArticleFromNarrativeClusterModalOpen: false,
        displayArticleActionBar: false,
        searchTerm, // Preserve searchTerm
        isAdvancedSearch, // Preserve isAdvancedSearch
        ordering, // Preserve ordering
      },
      false,
      "[User] Articles Management: Reset state",
    );
  },

  // Volume graph actions
  setFeedVolume: (volume) =>
    set({ feedVolume: volume }, false, "[User] Feed Content: Set feed volume"),

  setStackFeedVolumeBy: (stackBy) =>
    set(
      { stackFeedVolumeBy: stackBy },
      false,
      "[User] Feed Content: Set stack feed volume by",
    ),

  setActiveBar: (bar) =>
    set({ activeBar: bar }, false, "[User] Feed Content: Set active bar"),

  setChartSelection: (selection: string | null) =>
    set(
      { chartSelection: selection },
      false,
      "[User] Feed Content: Set chart selection",
    ),

  resetFeedVolumeView: () =>
    set(
      {
        feedVolume: null,
        activeBar: undefined,
        chartSelection: null,
      },
      false,
      "[User] Feed Content: Reset feed volume view",
    ),

  // Article selection actions
  toggleArticleSelection: (id: string) => {
    // Validate input
    if (!id || typeof id !== "string") {
      console.warn(
        "[Article Management] toggleArticleSelection: Invalid article ID",
        { id },
      );
      return;
    }

    const {
      selectedArticleIds,
      excludedArticleIds,
      isAllSelected,
      pagination,
      articlesMap,
    } = get();

    // Validate state integrity
    if (!articlesMap) {
      console.error(
        "[Article Management] toggleArticleSelection: articlesMap is missing",
      );
      return;
    }

    const newSelection = new Set(selectedArticleIds);

    if (isAllSelected) {
      const newExcluded = new Set(excludedArticleIds);

      if (newExcluded.has(id)) {
        newExcluded.delete(id);
      } else {
        newExcluded.add(id);
      }

      // Use pagination.totalCount if available, otherwise fallback to articlesMap.size
      // This matches the logic in buildSelectionSnapshot
      const totalArticles = pagination?.totalCount ?? articlesMap.size;
      const selectedCount = Math.max(totalArticles - newExcluded.size, 0);

      set(
        {
          excludedArticleIds: newExcluded,
          displayArticleActionBar: selectedCount > 0,
        },
        false,
        "[User] Article Management: Toggle selection (all selected)",
      );
      return;
    }

    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }

    set(
      {
        selectedArticleIds: newSelection,
        displayArticleActionBar: newSelection.size > 0,
        isAllSelected: false,
      },
      false,
      "[User] Article Management: Toggle selection",
    );
  },

  setSelectedArticles: (ids: Set<string>) => {
    // Validate input
    if (!(ids instanceof Set)) {
      console.error(
        "[Article Management] setSelectedArticles: Invalid ids parameter, expected Set",
        { ids },
      );
      return;
    }

    // Filter out invalid IDs
    const validIds = new Set<string>();
    for (const id of ids) {
      if (id && typeof id === "string" && id.trim().length > 0) {
        validIds.add(id);
      }
    }

    set(
      { selectedArticleIds: validIds },
      false,
      "[User] Article Management: Set selected articles",
    );
  },

  clearSelection: () => {
    set(
      {
        selectedArticleIds: new Set<string>(),
        excludedArticleIds: new Set<string>(),
        displayArticleActionBar: false,
        isAllSelected: false,
      },
      false,
      "[User] Article Management: Clear selection",
    );
  },

  setIsAllSelected: (isAllSelected: boolean) => {
    if (isAllSelected) {
      set(
        {
          isAllSelected: true,
          selectedArticleIds: new Set<string>(),
          excludedArticleIds: new Set<string>(),
          displayArticleActionBar: true,
        },
        false,
        "[User] Article Management: Enable select all",
      );
      return;
    }

    set(
      {
        isAllSelected: false,
        selectedArticleIds: new Set<string>(),
        excludedArticleIds: new Set<string>(),
        displayArticleActionBar: false,
      },
      false,
      "[User] Article Management: Set is all selected",
    );
  },

  // Delete actions
  setSingleArticleIdForAction: (id: string | null) => {
    set(
      { singleArticleIdForAction: id },
      false,
      "[User] Article Management: Set single article for action",
    );
  },

  setDeleteModalOpen: (open: boolean) => {
    set(
      { isDeleteModalOpen: open },
      false,
      "[User] Article Management: Set delete modal open",
    );
  },

  setEditModalOpen: (open: boolean) => {
    set(
      { isEditModalOpen: open },
      false,
      "[User] Article Management: Set edit modal open",
    );
  },

  setRemoveArticleFromClusterModalOpen: (open: boolean) => {
    set(
      { isRemoveArticleFromClusterModalOpen: open },
      false,
      "[User] Article Management: Set remove article from cluster modal open",
    );
  },

  setMoveArticleFromNarrativeClusterModalOpen: (open: boolean) => {
    set(
      { isMoveArticleFromNarrativeClusterModalOpen: open },
      false,
      "[User] Article Management: Set move article from narrative cluster modal open",
    );
  },

  setNarrativeIntelligenceV2ModalOpen: (open: boolean) => {
    set(
      { isNarrativeIntelligenceV2ModalOpen: open },
      false,
      "[User] Article Management: Set narrative intelligence V2 modal open",
    );
  },

  setInsightsNarrativeClusterId: (id: string) => {
    set(
      { insightsNarrativeClusterId: id },
      false,
      "[User] Article Management: Set insights narrative cluster ID",
    );
  },

  resetModalState: () => {
    set(
      {
        isDeleteModalOpen: false,
        isEditModalOpen: false,
        isRemoveArticleFromClusterModalOpen: false,
        isMoveArticleFromNarrativeClusterModalOpen: false,
        isNarrativeIntelligenceV2ModalOpen: false,
        insightsNarrativeClusterId: "",
        singleArticleIdForAction: null,
      },
      false,
      "[User] Article Management: Reset modal state",
    );
  },

  setDisplayArticleActionBar: (display: boolean) => {
    set(
      { displayArticleActionBar: display },
      false,
      "[User] Article Management: Set display article action bar",
    );
  },

  deleteArticlesFromStore: (ids: string[]) => {
    // Validate input
    if (!Array.isArray(ids)) {
      console.error(
        "[Article Management] deleteArticlesFromStore: Invalid ids parameter",
        { ids },
      );
      return;
    }

    if (ids.length === 0) {
      // No articles to delete, but this is valid - just return early
      return;
    }

    // Filter out invalid IDs
    const validIds = ids.filter(
      (id) => id && typeof id === "string" && id.trim().length > 0,
    );

    if (validIds.length === 0) {
      console.warn(
        "[Article Management] deleteArticlesFromStore: No valid IDs provided",
        { ids },
      );
      return;
    }

    // If some IDs were invalid, log a warning
    if (validIds.length !== ids.length) {
      console.warn(
        "[Article Management] deleteArticlesFromStore: Some invalid IDs filtered out",
        {
          total: ids.length,
          valid: validIds.length,
          invalid: ids.length - validIds.length,
        },
      );
    }

    const {
      articlesMap,
      currentPage,
      pagination,
      selectedArticleIds,
      excludedArticleIds,
      singleArticleIdForAction,
      resetSelectionAfterBulkAction,
    } = get();

    // Validate state integrity
    if (!articlesMap) {
      console.error(
        "[Article Management] deleteArticlesFromStore: articlesMap is missing",
      );
      return;
    }

    if (!currentPage) {
      console.error(
        "[Article Management] deleteArticlesFromStore: currentPage is missing",
      );
      return;
    }

    try {
      const newMap = new Map(articlesMap);
      const newSelection = new Set(selectedArticleIds);
      const newExcluded = new Set(excludedArticleIds);

      // Count how many articles were actually deleted
      let deletedCount = 0;
      for (const id of validIds) {
        if (newMap.has(id)) {
          deletedCount++;
        }
        newMap.delete(id);
        newSelection.delete(id);
        newExcluded.delete(id);
      }

      // Remove from current page edges
      const newEdges = currentPage.edges.filter(
        ({ node }) => !validIds.includes(node.id),
      );

      // Update total count
      const newTotalCount =
        pagination?.totalCount !== null && pagination?.totalCount !== undefined
          ? Math.max(0, pagination.totalCount - deletedCount)
          : null;

      const isSingleDelete = singleArticleIdForAction !== null;

      set(
        {
          articlesMap: newMap,
          currentPage: {
            ...currentPage,
            edges: newEdges,
          },
          pagination: {
            ...pagination,
            totalCount: newTotalCount,
          },
          selectedArticleIds: newSelection,
          excludedArticleIds: newExcluded,
          singleArticleIdForAction: null,
          isDeleteModalOpen: false,
        },
        false,
        "[User] Article Management: Delete articles from store",
      );

      resetSelectionAfterBulkAction(isSingleDelete);
    } catch (error) {
      console.error(
        "[Article Management] deleteArticlesFromStore: Error during deletion",
        {
          error,
          ids: validIds,
        },
      );
      // Don't throw - fail gracefully to prevent state corruption
    }
  },

  // Article Edit Actions
  setEditField: <K extends keyof ArticleEditState>(
    field: K,
    value: ArticleEditState[K],
  ) => {
    const { editArticle } = get();
    set(
      { editArticle: { ...editArticle, [field]: value } },
      false,
      `[User] Article Edit: Set ${String(field)}`,
    );
  },

  setEditFields: (fields: Partial<ArticleEditState>) => {
    const { editArticle } = get();
    set(
      { editArticle: { ...editArticle, ...fields } },
      false,
      "[User] Article Edit: Set multiple fields",
    );
  },

  clearEditFields: () => {
    set(
      {
        editArticle: {
          author: null,
          category: null,
          datePublished: null,
          headline: null,
          isLede: null,
          prominence: null,
          publisher: null,
          sentimentPolarity: null,
          sentimentRationale: null,
          originalAiSentiment: null,
          summary: null,
          articleTagIds: [],
        },
      },
      false,
      "[User] Article Edit: Clear all fields",
    );
  },

  resetEditModal: () => {
    const { resetModalState, clearEditFields } = get();
    resetModalState();
    clearEditFields();
  },

  populateEditFieldsForSingleArticle: (articleId: string) => {
    const { articlesMap } = get();
    const article = articlesMap.get(articleId);

    if (!article) {
      console.warn(`Article with ID ${articleId} not found in articlesMap`);
      return;
    }

    const editFields: ArticleEditState = {
      author: article.authors?.[0] ?? null,
      category: article.contentCategory ?? null,
      datePublished: (() => {
        if (!article.datePublished) return null;
        try {
          const parsedDate = parseISO(article.datePublished);
          return formatInTimeZone(parsedDate, "UTC", "yyyy-MM-dd");
        } catch {
          return null;
        }
      })(),
      headline: article.headline ?? null,
      isLede: article.isLede ?? null,
      prominence: article.prominence ?? null,
      publisher: article.publisher ?? null,
      sentimentPolarity: article.sentimentPolarity ?? null,
      sentimentRationale: article.sentimentRationale ?? null,
      originalAiSentiment:
        article.sentimentPolarityOriginal && article.sentimentRationaleOriginal
          ? {
              polarity: article.sentimentPolarityOriginal,
              rationale: article.sentimentRationaleOriginal,
            }
          : null,
      summary: article.summary ?? null,
      articleTagIds: article.articleTags.map((tag) => tag.id),
    };

    // Set all edit fields at once by overriding the editArticle state
    set(
      { editArticle: editFields },
      false,
      "[User] Article Edit: Populate fields for single article",
    );
  },

  updateArticlesInStore: (
    updatedArticles: GetFeedArticlesPagedFromProjectionArticle[],
  ) => {
    const { articlesMap, currentPage } = get();

    // Update articles in the map
    const newMap = new Map(articlesMap);
    for (const article of updatedArticles) {
      newMap.set(article.id, article);
    }

    // Update articles in currentPage edges
    const newEdges = currentPage.edges.map((edge) => {
      const updatedArticle = updatedArticles.find(
        (updated) => updated.id === edge.node.id,
      );
      return updatedArticle ? { ...edge, node: updatedArticle } : edge;
    });

    set(
      {
        articlesMap: newMap,
        currentPage: {
          ...currentPage,
          edges: newEdges,
        },
      },
      false,
      "[User] Article Edit: Update articles in store",
    );
  },
  resetSelectionAfterBulkAction: (isSingleAction: boolean) => {
    // Validate input
    if (typeof isSingleAction !== "boolean") {
      console.error(
        "[Article Management] resetSelectionAfterBulkAction: Invalid isSingleAction parameter",
        { isSingleAction },
      );
      return;
    }

    try {
      const {
        selectedArticleIds,
        excludedArticleIds,
        isAllSelected,
        pagination,
        articlesMap,
      } = get();

      // Validate state integrity
      if (!articlesMap) {
        console.error(
          "[Article Management] resetSelectionAfterBulkAction: articlesMap is missing",
        );
        return;
      }

      // Ensure Sets are valid
      const safeSelectedIds =
        selectedArticleIds instanceof Set
          ? selectedArticleIds
          : new Set<string>();
      const safeExcludedIds =
        excludedArticleIds instanceof Set
          ? excludedArticleIds
          : new Set<string>();

      const hasArticlesRemaining =
        pagination?.totalCount !== null && pagination?.totalCount !== undefined
          ? pagination.totalCount > 0
          : articlesMap.size > 0;

      if (isSingleAction) {
        const nextIsAllSelected = isAllSelected && hasArticlesRemaining;
        const nextExcludedArticleIds = nextIsAllSelected
          ? safeExcludedIds
          : new Set<string>();
        const nextDisplayArticleActionBar = nextIsAllSelected
          ? hasArticlesRemaining
          : safeSelectedIds.size > 0;

        set(
          {
            excludedArticleIds: nextExcludedArticleIds,
            isAllSelected: nextIsAllSelected,
            displayArticleActionBar: nextDisplayArticleActionBar,
          },
          false,
          "[User] Article Management: Maintain selection after single action",
        );
      } else {
        set(
          {
            selectedArticleIds: new Set<string>(),
            excludedArticleIds: new Set<string>(),
            isAllSelected: false,
            displayArticleActionBar: false,
          },
          false,
          "[User] Article Management: Reset selection after bulk action",
        );
      }
    } catch (error) {
      console.error(
        "[Article Management] resetSelectionAfterBulkAction: Error during reset",
        {
          error,
          isSingleAction,
        },
      );
      // Fail gracefully - reset to safe state
      set(
        {
          selectedArticleIds: new Set<string>(),
          excludedArticleIds: new Set<string>(),
          isAllSelected: false,
          displayArticleActionBar: false,
        },
        false,
        "[User] Article Management: Reset selection after error",
      );
    }
  },

  // Search actions
  setSearchTerm: (searchTerm: string) => {
    set({ searchTerm }, false, "[User] Feed Content: Set search term");
  },

  setIsAdvancedSearch: (isAdvancedSearch: boolean) => {
    set(
      { isAdvancedSearch },
      false,
      "[User] Feed Content: Set is advanced search",
    );
  },

  // Ordering actions
  setOrdering: (ordering: FeedArticleProjectionOrdering) => {
    set({ ordering }, false, "[User] Feed Content: Set ordering");
  },
});
